import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { analyticsService } from '@/services/analytics/analyticsService';
import type {
  AssessmentAttemptsResponse,
  AttemptQuestionAnalytics,
  AssessmentStatisticsResponse,
  AnalyticsDashboard,
  AssessmentAttemptsQuery,
  StatisticsQuery,
  DashboardQuery,
} from '@/services/analytics/analyticsService';

// Analytics State Interface
interface AnalyticsState {
  // Data
  attemptsData: AssessmentAttemptsResponse | null;
  attemptQuestionData: AttemptQuestionAnalytics | null;
  statisticsData: AssessmentStatisticsResponse | null;
  dashboardData: AnalyticsDashboard | null;
  
  // Loading states
  attemptsLoading: boolean;
  attemptQuestionLoading: boolean;
  statisticsLoading: boolean;
  dashboardLoading: boolean;
  exportLoading: boolean;
  
  // Error states
  attemptsError: string | null;
  attemptQuestionError: string | null;
  statisticsError: string | null;
  dashboardError: string | null;
  exportError: string | null;
}

// Initial State
const initialState: AnalyticsState = {
  // Data
  attemptsData: null,
  attemptQuestionData: null,
  statisticsData: null,
  dashboardData: null,
  
  // Loading states
  attemptsLoading: false,
  attemptQuestionLoading: false,
  statisticsLoading: false,
  dashboardLoading: false,
  exportLoading: false,
  
  // Error states
  attemptsError: null,
  attemptQuestionError: null,
  statisticsError: null,
  dashboardError: null,
  exportError: null,
};

// Async Thunks
export const fetchAssessmentAttempts = createAsyncThunk(
  'analytics/fetchAssessmentAttempts',
  async (params: AssessmentAttemptsQuery | undefined, { rejectWithValue }) => {
    try {
      const response = await analyticsService.getAssessmentAttempts(params);
      
      if (response.success && response.data) {
        return response.data;
      } else {
        return rejectWithValue(response.message || 'Failed to fetch assessment attempts');
      }
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || error.message || 'Failed to fetch assessment attempts');
    }
  }
);

export const fetchAttemptQuestionAnalytics = createAsyncThunk(
  'analytics/fetchAttemptQuestionAnalytics',
  async (attemptId: string, { rejectWithValue }) => {
    try {
      const response = await analyticsService.getAttemptQuestionAnalytics(attemptId);
      
      if (response.success && response.data) {
        return response.data;
      } else {
        return rejectWithValue(response.message || 'Failed to fetch question analytics');
      }
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || error.message || 'Failed to fetch question analytics');
    }
  }
);

export const fetchAssessmentStatistics = createAsyncThunk(
  'analytics/fetchAssessmentStatistics',
  async (params: StatisticsQuery | undefined, { rejectWithValue }) => {
    try {
      const response = await analyticsService.getAssessmentStatistics(params);
      
      if (response.success && response.data) {
        return response.data;
      } else {
        return rejectWithValue(response.message || 'Failed to fetch statistics');
      }
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || error.message || 'Failed to fetch statistics');
    }
  }
);

export const fetchAnalyticsDashboard = createAsyncThunk(
  'analytics/fetchAnalyticsDashboard',
  async (params: DashboardQuery | undefined, { rejectWithValue }) => {
    try {
      const response = await analyticsService.getAnalyticsDashboard(params);
      
      if (response.success && response.data) {
        return response.data;
      } else {
        return rejectWithValue(response.message || 'Failed to fetch dashboard data');
      }
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || error.message || 'Failed to fetch dashboard data');
    }
  }
);

export const exportAssessmentAttempts = createAsyncThunk(
  'analytics/exportAssessmentAttempts',
  async (params: { query?: AssessmentAttemptsQuery; format: 'csv' | 'xlsx' }, { rejectWithValue }) => {
    try {
      const blob = await analyticsService.exportAssessmentAttempts(params.query, params.format);
      
      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `assessment-attempts.${params.format}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      return { success: true, message: 'Export completed successfully' };
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || error.message || 'Export failed');
    }
  }
);

// Analytics Slice
export const analyticsSlice = createSlice({
  name: 'analytics',
  initialState,
  reducers: {
    // Clear data
    clearAttemptsData: (state) => {
      state.attemptsData = null;
      state.attemptsError = null;
    },
    
    clearAttemptQuestionData: (state) => {
      state.attemptQuestionData = null;
      state.attemptQuestionError = null;
    },
    
    clearStatisticsData: (state) => {
      state.statisticsData = null;
      state.statisticsError = null;
    },
    
    clearDashboardData: (state) => {
      state.dashboardData = null;
      state.dashboardError = null;
    },
    
    clearAllAnalyticsData: (state) => {
      state.attemptsData = null;
      state.attemptQuestionData = null;
      state.statisticsData = null;
      state.dashboardData = null;
      state.attemptsError = null;
      state.attemptQuestionError = null;
      state.statisticsError = null;
      state.dashboardError = null;
      state.exportError = null;
    },
    
    // Clear errors
    clearAttemptsError: (state) => {
      state.attemptsError = null;
    },
    
    clearAttemptQuestionError: (state) => {
      state.attemptQuestionError = null;
    },
    
    clearStatisticsError: (state) => {
      state.statisticsError = null;
    },
    
    clearDashboardError: (state) => {
      state.dashboardError = null;
    },
    
    clearExportError: (state) => {
      state.exportError = null;
    },
    
    clearAllErrors: (state) => {
      state.attemptsError = null;
      state.attemptQuestionError = null;
      state.statisticsError = null;
      state.dashboardError = null;
      state.exportError = null;
    },
  },
  extraReducers: (builder) => {
    // Fetch Assessment Attempts
    builder
      .addCase(fetchAssessmentAttempts.pending, (state) => {
        state.attemptsLoading = true;
        state.attemptsError = null;
      })
      .addCase(fetchAssessmentAttempts.fulfilled, (state, action: PayloadAction<AssessmentAttemptsResponse>) => {
        state.attemptsLoading = false;
        state.attemptsData = action.payload;
        state.attemptsError = null;
      })
      .addCase(fetchAssessmentAttempts.rejected, (state, action) => {
        state.attemptsLoading = false;
        state.attemptsError = action.payload as string;
      })
    
    // Fetch Attempt Question Analytics
    builder
      .addCase(fetchAttemptQuestionAnalytics.pending, (state) => {
        state.attemptQuestionLoading = true;
        state.attemptQuestionError = null;
      })
      .addCase(fetchAttemptQuestionAnalytics.fulfilled, (state, action: PayloadAction<AttemptQuestionAnalytics>) => {
        state.attemptQuestionLoading = false;
        state.attemptQuestionData = action.payload;
        state.attemptQuestionError = null;
      })
      .addCase(fetchAttemptQuestionAnalytics.rejected, (state, action) => {
        state.attemptQuestionLoading = false;
        state.attemptQuestionError = action.payload as string;
      })
    
    // Fetch Assessment Statistics
    builder
      .addCase(fetchAssessmentStatistics.pending, (state) => {
        state.statisticsLoading = true;
        state.statisticsError = null;
      })
      .addCase(fetchAssessmentStatistics.fulfilled, (state, action: PayloadAction<AssessmentStatisticsResponse>) => {
        state.statisticsLoading = false;
        state.statisticsData = action.payload;
        state.statisticsError = null;
      })
      .addCase(fetchAssessmentStatistics.rejected, (state, action) => {
        state.statisticsLoading = false;
        state.statisticsError = action.payload as string;
      })
    
    // Fetch Analytics Dashboard
    builder
      .addCase(fetchAnalyticsDashboard.pending, (state) => {
        state.dashboardLoading = true;
        state.dashboardError = null;
      })
      .addCase(fetchAnalyticsDashboard.fulfilled, (state, action: PayloadAction<AnalyticsDashboard>) => {
        state.dashboardLoading = false;
        state.dashboardData = action.payload;
        state.dashboardError = null;
      })
      .addCase(fetchAnalyticsDashboard.rejected, (state, action) => {
        state.dashboardLoading = false;
        state.dashboardError = action.payload as string;
      })
    
    // Export Assessment Attempts
    builder
      .addCase(exportAssessmentAttempts.pending, (state) => {
        state.exportLoading = true;
        state.exportError = null;
      })
      .addCase(exportAssessmentAttempts.fulfilled, (state) => {
        state.exportLoading = false;
        state.exportError = null;
      })
      .addCase(exportAssessmentAttempts.rejected, (state, action) => {
        state.exportLoading = false;
        state.exportError = action.payload as string;
      });
  },
});